import { useCallback, useMemo } from "react";
import moment from "moment-timezone";
import type {
  Action,
  CalendarAddDisplayOptions,
  SelectableOption,
} from "@core/features/actions";
import {
  useGetSchedulingAvailability,
  getSelectedRepeatingOptionsFromSlots,
} from "@core/features/scheduling";
import { useUserMeetings } from "@platform/hooks/useUserMeetings";
import { useUserTimezoneOption } from "@platform/hooks/useUserTimezoneOption";
import { ProgramCalendarEventList } from "./ProgramCalendarEventList";

export type Props = {
  /** The calendar_add action */
  calendarAddAction: Action;
};

/**
 * Process selectableOptions: generate IDs and compute day from date
 */
function processSelectableOptions(selectableOptions: SelectableOption[]) {
  return selectableOptions.map((opt, index) => ({
    ...opt,
    id: opt.id || `opt-${opt.date}-${opt.startHour}-${opt.startMinute}-${index}`,
    day: opt.day ?? moment.utc(opt.date).day(),
  }));
}

/**
 * Container for ProgramCalendarEventList.
 * Handles data fetching and determines whether to show meetings or scheduled times.
 *
 * Logic:
 * 1. If user has meetings, display those (meetings take precedence)
 * 2. Otherwise, if action has selectableOptions in display_options and user has
 *    selected some via scheduling, display those selected options
 * 3. Otherwise, render nothing
 */
export function ProgramCalendarEventListContainer({ calendarAddAction }: Props) {
  // Get user's meetings
  const { meetings } = useUserMeetings();

  // Get user's timezone
  const userTimezoneOption = useUserTimezoneOption();

  // Extract selectableOptions, eventTitle, and eventDescription from action's display_options
  const { selectableOptions, eventTitle, eventDescription } = useMemo(() => {
    const displayOptions =
      calendarAddAction.display_options as CalendarAddDisplayOptions | null;

    return {
      selectableOptions: displayOptions?.selectableOptions
        ? processSelectableOptions(displayOptions.selectableOptions)
        : undefined,
      eventTitle:
        displayOptions?.eventTitle ||
        calendarAddAction.name ||
        "Peer Circle Meeting",
      eventDescription: displayOptions?.eventDescription,
    };
  }, [calendarAddAction]);

  // Fetch user's saved scheduling availability
  const { data: savedTimeSlots } = useGetSchedulingAvailability(
    calendarAddAction.program
  );

  // Determine which selectableOptions are selected based on saved slots
  // Uses the shared utility for repeating mode selection logic
  const selectedOptions = useMemo(() => {
    if (!selectableOptions || !savedTimeSlots?.length) {
      return undefined;
    }
    const selected = getSelectedRepeatingOptionsFromSlots(
      selectableOptions,
      savedTimeSlots
    );
    return selected.length > 0 ? selected : undefined;
  }, [selectableOptions, savedTimeSlots]);

  // Handler for adding calendar events
  const handleAddCalendarEvent = useCallback(
    (
      _eventId: number | string | "all",
      _calendarType: "google" | "ical"
    ) => {
      // Calendar event handling is done within the calendar buttons themselves
      // This callback can be used for analytics/tracking if needed
    },
    []
  );

  // Determine what to render
  const hasMeetings = meetings && meetings.length > 0;
  const hasSelectedOptions = selectedOptions && selectedOptions.length > 0;

  // Return null if nothing to display
  if (!hasMeetings && !hasSelectedOptions) {
    return null;
  }

  return (
    <ProgramCalendarEventList
      meetings={hasMeetings ? meetings : undefined}
      selectableOptions={!hasMeetings ? selectedOptions : undefined}
      eventTitle={eventTitle}
      eventDescription={eventDescription}
      userTimezone={userTimezoneOption ?? undefined}
      onAddCalendarEvent={handleAddCalendarEvent}
    />
  );
}

export default ProgramCalendarEventListContainer;
